"use client";

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useUsageLedger } from "@/lib/usage/usage.hooks";
import { useLocalStorageState } from "@/lib/useLocalStorageState";

/** Kept stable so prior dismissals continue to suppress the promo. */
export const WELCOME_PROMO_DISMISSED_KEY = "dismissed-promo-welcome-credit";

const REVEAL_DURATION_MS = 1200;

type WelcomeCreditRevealContextValue = {
  /**
   * Amount shown in the welcome dialog copy, in USD cents.
   * Equals the current unused credit balance (sum of ledger grants when there is
   * no usage yet) — covers automatic welcome credit and admin invite grants.
   */
  promoAmountCents: number;
  /** Configured system welcome credit amount (for low-credit threshold). */
  configuredWelcomeAmountCents: number;
  shouldShowPromo: boolean;
  /** True while eligibility is still loading — callers should avoid flashing real balances. */
  isEligibilityPending: boolean;
  displayBalanceMicro: (realMicro: string | number | null | undefined) => number;
  /** Dismiss the promo and animate balances from $0 to `targetMicro` (real balance). */
  reveal: (targetMicro?: number) => void;
};

const WelcomeCreditRevealContext = createContext<WelcomeCreditRevealContextValue | null>(null);

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

export const WelcomeCreditRevealProvider = ({ children }: { children: ReactNode }) => {
  const { user, billingSummary } = useAuth();
  const [dismissed, setDismissed] = useLocalStorageState<boolean>(WELCOME_PROMO_DISMISSED_KEY, false, {
    dataType: "boolean",
    initializeWithValue: false,
  });
  const [hydrated, setHydrated] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [animatedMicro, setAnimatedMicro] = useState(0);
  const latestRealMicroRef = useRef(0);
  const revealTargetRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const {
    data: ledgerData,
    isSuccess: ledgerSuccess,
    isPending: ledgerPending,
  } = useUsageLedger(1, null, null, {
    enabled: !!user,
  });

  const {
    data: billingData,
    isSuccess: billingSuccess,
    isPending: billingPending,
  } = useBillingOverview({
    enabled: !!user,
  });

  // After a paid top-up / second grant, stop masking — otherwise Billing keeps showing
  // $0.00 and users think Checkout failed. Also unmask immediately on Checkout return
  // (?checkout=success) before the webhook has incremented positive_credit_entry_count.
  const checkoutJustSucceeded =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("checkout") === "success";

  useEffect(() => {
    setHydrated(true);
  }, []);

  // Persist unmask after Checkout return so the balance does not remask when the
  // success query param is cleared before the webhook bumps positive_credit_entry_count.
  useEffect(() => {
    if (!checkoutJustSucceeded || dismissed) {
      return;
    }
    setDismissed(true);
  }, [checkoutJustSucceeded, dismissed, setDismissed]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const balanceMicro = Number(billingData?.billing_overview?.credit_balance_micro ?? 0);
  const safeBalanceMicro = Number.isFinite(balanceMicro) ? Math.max(0, balanceMicro) : 0;
  // micro-USD → cents: 1 cent = 10_000 micro
  const promoAmountCents = Math.round(safeBalanceMicro / 10_000);
  const configuredWelcomeAmountCents = billingSummary?.welcome_credit_amount_cents ?? 0;

  const hasUsage = (ledgerData?.usage_ledger?.events?.length ?? 0) > 0;
  const positiveCreditEntryCount = billingData?.billing_overview?.positive_credit_entry_count ?? 0;
  const hasOnlyInitialGrant = positiveCreditEntryCount <= 1 && !checkoutJustSucceeded;
  const eligibilityReady = hydrated && !!user && ledgerSuccess && billingSuccess;
  const isEligibilityPending = !!user && (!hydrated || ledgerPending || billingPending);

  // Show whenever the org still has unused initial grant credit and the promo
  // hasn't been dismissed — amount comes from the live balance (welcome and/or
  // admin invite grants), not the configured welcome default.
  const eligibleForPromo =
    eligibilityReady && promoAmountCents > 0 && !hasUsage && hasOnlyInitialGrant && !dismissed && !isRevealing;

  const shouldMaskBalance = eligibleForPromo;

  const startRevealAnimation = useCallback((targetMicro: number) => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
    }
    revealTargetRef.current = Math.max(0, targetMicro);
    setIsRevealing(true);
    setAnimatedMicro(0);
    const startedAt = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / REVEAL_DURATION_MS);
      const eased = easeOutCubic(progress);
      setAnimatedMicro(Math.round(revealTargetRef.current * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        setAnimatedMicro(revealTargetRef.current);
        setIsRevealing(false);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const reveal = useCallback(
    (targetMicro?: number) => {
      if (dismissed && !isRevealing) {
        return;
      }
      setDismissed(true);
      const target =
        targetMicro !== undefined && Number.isFinite(targetMicro)
          ? Math.max(0, targetMicro)
          : latestRealMicroRef.current;
      startRevealAnimation(target);
    },
    [dismissed, isRevealing, setDismissed, startRevealAnimation],
  );

  const displayBalanceMicro = useCallback(
    (realMicro: string | number | null | undefined): number => {
      const real = Number(realMicro ?? 0);
      const safeReal = Number.isFinite(real) ? real : 0;
      latestRealMicroRef.current = safeReal;

      // Hard rule: once the promo is dismissed (localStorage), never fake the balance.
      if (dismissed) {
        return isRevealing ? animatedMicro : safeReal;
      }

      if (isRevealing) {
        return animatedMicro;
      }

      // `dismissed` initializes as false until localStorage syncs (initializeWithValue:
      // false). React Query can still return a cached balance on this first paint.
      // Sync-read the dismiss key so we neither spoil the welcome reveal for new
      // users nor mask orgs that already dismissed the promo.
      if (!hydrated) {
        if (typeof window !== "undefined") {
          try {
            if (window.localStorage.getItem(WELCOME_PROMO_DISMISSED_KEY) === "true") {
              return safeReal;
            }
          } catch {
            // private mode / blocked storage — fall through to mask
          }
        }
        return 0;
      }

      // Fail closed only after we know the promo is still undismissed.
      if (isEligibilityPending || shouldMaskBalance) {
        return 0;
      }

      return safeReal;
    },
    [animatedMicro, dismissed, hydrated, isEligibilityPending, isRevealing, shouldMaskBalance],
  );

  const value = useMemo<WelcomeCreditRevealContextValue>(
    () => ({
      promoAmountCents,
      configuredWelcomeAmountCents,
      shouldShowPromo: eligibleForPromo,
      isEligibilityPending,
      displayBalanceMicro,
      reveal,
    }),
    [
      promoAmountCents,
      configuredWelcomeAmountCents,
      eligibleForPromo,
      isEligibilityPending,
      displayBalanceMicro,
      reveal,
    ],
  );

  return <WelcomeCreditRevealContext.Provider value={value}>{children}</WelcomeCreditRevealContext.Provider>;
};

export const useWelcomeCreditReveal = (): WelcomeCreditRevealContextValue => {
  const ctx = useContext(WelcomeCreditRevealContext);
  if (!ctx) {
    throw new Error("useWelcomeCreditReveal must be used within WelcomeCreditRevealProvider");
  }
  return ctx;
};

/** Safe variant for surfaces that may render outside the app shell (returns passthrough). */
export const useOptionalWelcomeCreditReveal = (): WelcomeCreditRevealContextValue | null =>
  useContext(WelcomeCreditRevealContext);
